import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TextField } from '@/components/text-field';
import { SelectField } from '@/components/select-field';
import { fetchContexts } from '@/services/context';
import { useForm, useWatch } from 'react-hook-form';
import { postEntryPoint } from '@/services/campaign';
import { postEntryPoint as postTrackingLinkEntryPoint } from '@/services/tracking-link';
import { useTranslation } from 'react-i18next';
import { cn, errorHandler } from '@/lib/utils';
import { PlusSquare } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
export const EntryLinkDialog = ({ campaign, trackingLink, isIcon, onSuccess }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const form = useForm({
        defaultValues: {
            name: '',
            context: '0',
        },
    });
    const selectedContext = useWatch({
        control: form.control,
        name: 'context',
    });
    const { data: contexts = [] } = useQuery({
        queryKey: ['contexts', 'selectable'],
        queryFn: fetchContexts,
        enabled: open,
        staleTime: 10 * 60 * 1000,
    });
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => {
            if (campaign) {
                return postEntryPoint({ publicId: campaign.publicId, data });
            }
            return postTrackingLinkEntryPoint({ publicId: trackingLink.publicId, data });
        },
        onSuccess: (data) => {
            toast.success(data.message);
            setOpen(false);
            onSuccess?.();
        },
        onError: errorHandler,
    });
    const onSubmit = React.useCallback((data) => {
        mutate(data);
    }, [mutate]);
    const options = React.useMemo(() => contexts.map((context) => ({ label: context.label, value: context.id.toString() })), [contexts]);
    const isContextSelected = selectedContext && selectedContext !== '0';
    return (<Dialog open={open} onOpenChange={(open) => {
            if (open) {
                form.reset({ name: '', context: '0' });
            }
            setOpen(open);
        }}>
            <DialogTrigger asChild>
                {isIcon ? (<Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => setOpen((prevState) => !prevState)}>
                                <PlusSquare />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('entry_points.new.title', { ns: 'glossary' })}</p>
                        </TooltipContent>
                    </Tooltip>) : (<Button variant="outline" size="sm">
                        {t('entry_points.new.title', { ns: 'glossary' })}
                    </Button>)}
            </DialogTrigger>
            <DialogContent className="w-[90vw] max-w-[90vw] sm:min-w-105 sm:max-w-125 md:min-w-130 md:max-w-160 lg:min-w-160 lg:max-w-180">
                <DialogHeader>
                    <DialogTitle>{t('entry_points.new.title', { ns: 'glossary' })}</DialogTitle>
                    <DialogDescription>{t('entry_points.new.help', { ns: 'glossary' })}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4">
                        <SelectField control={form.control} name="context" label={t('entry_points.new.context.label', { ns: 'glossary' })} disabled={isPending} placeholder={t('placeholder.select.locale', { ns: 'glossary' })} options={options} description={t('entry_points.new.context.help', { ns: 'glossary' })} onChange={(value) => {
            const context = contexts.find((context) => context.id.toString() === value);
            form.setValue('name', context?.label || '');
        }}/>
                        <div className={cn('transition-all duration-200 ease-out', {
            'opacity-100 translate-x-0': isContextSelected,
            'opacity-0 -translate-x-1 overflow-hidden pointer-events-none': !isContextSelected,
        })}>
                            <TextField disabled={isPending} control={form.control} name="name" label={t('entry_points.new.key_name.label', { ns: 'glossary' })} placeholder={t('entry_points.new.key_name.placeholder', { ns: 'glossary' })} description={t('entry_points.new.key_name.help', { ns: 'glossary' })}/>
                        </div>
                    </form>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                            {t('cancel')}
                        </Button>
                        <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending || !isContextSelected}>
                            {isPending ? t('adding') : t('add')}
                        </Button>
                    </div>
                </Form>
            </DialogContent>
        </Dialog>);
};
